import { tenantRetention } from '@/utils/tenantIrpf';
import { formatLocalDate } from '@/utils/localDateUtils';
import { ownerIrpfStatus, type IrpfConfig } from '@/utils/irpfPolicy';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import contractService from '@/services/contractService';
import paymentService from '@/services/paymentService';
import reciboService from '@/services/reciboService';
import configService from '@/services/configService';
import dgiService from '@/services/dgiService';
import ReceiptIssueDialog from '@/components/recibos/ReceiptIssueDialog';
import { ReceiptIssuance, type IssueTask } from '@/utils/receiptIssuance';
import { getCfeRequestError } from '@/utils/cfeUtils';
import { Contrato, PropietarioContrato } from '@/types/contrato';
import { Pago, METODOS_PAGO } from '@/types';
import { useDialog } from '@/hooks/useDialog';

type TipoRecibo = 'INQUILINO' | 'PROPIETARIO';

const CONTRACT_PAGE_SIZE = 10;

interface PendingPayment extends Pago {
  selected: boolean;
}

interface PropietarioBreakdown {
  propietario: PropietarioContrato;
  valor_alquiler: number;
  comision: number;
  iva_comision: number;
  retencion_irpf: number;
  retencion_irpf_inquilino: number;
  interes_mora_share: number;
  iva_interes_share: number;
  otros_importes_share: number;
  neto: number;
  monto_aplicado_pago: number;
}

export default function ReciboCreate() {
  const navigate = useNavigate();
  const { showAlert } = useDialog();
  const [step, setStep] = useState<'select-contract' | 'fill-data'>('select-contract');
  const [tipoRecibo, setTipoRecibo] = useState<TipoRecibo>('INQUILINO');

  // Contract selection
  const [contracts, setContracts] = useState<Contrato[]>([]);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contrato | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [contractPage, setContractPage] = useState(1);
  const [contractTotal, setContractTotal] = useState(0);
  const [contractTotalPages, setContractTotalPages] = useState(1);
  const skipContractSearchEffect = useRef(true);

  // Pending payments
  const [pendingPayments, setPendingPayments] = useState<PendingPayment[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<PendingPayment | null>(null);

  // Form data for INQUILINO
  const [inquilinoForm, setInquilinoForm] = useState({
    valor_alquiler: 0,
    interes_mora: 0,
    iva_interes: 0,
    otros_importes: [] as Array<{ descripcion: string; valor: number }>,
    metodo_pago: ''
  });

  // Shared form data for PROPIETARIO (fields that apply across all propietarios)
  const [propSharedForm, setPropSharedForm] = useState({
    metodo_pago: '',
    otros_importes: [] as Array<{ descripcion: string; valor: number }>,
    interes_mora: 0,
    iva_interes: 0,
    resguardo_confirmado: false,
    resguardo_numero: '',
  });

  // Base amount for propietario (set when payment is selected)
  const [propMontoBase, setPropMontoBase] = useState(0);

  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);
  const [createdReceiptIds, setCreatedReceiptIds] = useState<number[]>([]);
  const [printError, setPrintError] = useState('');
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueTasks, setIssueTasks] = useState<IssueTask[]>([]);
  const [issueCompleted, setIssueCompleted] = useState(false);
  const [canReturnToForm, setCanReturnToForm] = useState(false);
  const issuance = useRef<ReceiptIssuance | null>(null);
  const issueController = useRef<AbortController | null>(null);
  const createForIssue = useRef<(() => Promise<number[]>) | null>(null);
  const printAfterIssue = useRef(false);

  useEffect(() => () => issueController.current?.abort(), []);
  useEffect(() => {
    if (!issueOpen || issueCompleted) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [issueOpen, issueCompleted]);
  const [systemIrpfPorcentaje, setSystemIrpfPorcentaje] = useState(0);
  const [irpfConfig, setIrpfConfig] = useState<IrpfConfig>({});
  const [irpfConfigLoaded, setIrpfConfigLoaded] = useState(false);
  const [tenantResguardo, setTenantResguardo] = useState({ confirmado: false, numero: '' });
  const tenantAgent = !!(tipoRecibo === 'INQUILINO'
    ? selectedPayment?.inquilino_agente_retencion_irpf ?? selectedContract?.inquilino_agente_retencion_irpf
    : selectedContract?.inquilino_agente_retencion_irpf);
  const irpfPending = tipoRecibo === 'PROPIETARIO' && !tenantAgent && (selectedContract?.propietarios || []).some(
    owner => ownerIrpfStatus(owner, Number(selectedPayment?.base_alquiler_recibo ?? selectedContract?.monto_alquiler_inquilino ?? 0) * (owner.porcentaje_propiedad ?? 100) / 100, selectedContract?.moneda_inquilino ?? 'UYU', irpfConfig) === null
  );

  const retencionIrpfInquilino = tenantRetention(inquilinoForm.valor_alquiler, systemIrpfPorcentaje, tenantAgent);

  // Calculate total for INQUILINO (memoized for performance)
  // For INQUILINO: ADD ALL fields (all are charges to tenant)
  const totalInquilino = useMemo(() => {
    const valorAlquiler = Number(inquilinoForm.valor_alquiler) || 0;
    const interesMora = Number(inquilinoForm.interes_mora) || 0;
    const ivaInteres = Number(inquilinoForm.iva_interes) || 0;
    const otrosTotal = inquilinoForm.otros_importes.reduce((sum, item) => sum + (Number(item.valor) || 0), 0);

    return valorAlquiler + interesMora + ivaInteres - retencionIrpfInquilino + otrosTotal;
  }, [
    inquilinoForm.valor_alquiler,
    inquilinoForm.interes_mora,
    inquilinoForm.iva_interes,
    retencionIrpfInquilino,
    inquilinoForm.otros_importes
  ]);

  // Compute per-propietario breakdowns based on ownership %
  const propietarioBreakdowns: PropietarioBreakdown[] = useMemo(() => {
    const propietarios = selectedContract?.propietarios || [];
    if (propietarios.length === 0 || propMontoBase === 0 || !selectedPayment) return [];

    const comisionRate = Number(selectedContract?.comision ?? 10) / 100;
    const ivaRate = Number(selectedContract?.iva ?? selectedContract?.default_iva ?? 22) / 100;
    const irpfRate = systemIrpfPorcentaje / 100;
    const otrosTotal = propSharedForm.otros_importes.reduce((sum, item) => sum + (Number(item.valor) || 0), 0);
    const interesMora = Number(propSharedForm.interes_mora) || 0;
    const ivaInteres = Number(propSharedForm.iva_interes) || 0;
    const pagoMonto = selectedPayment.monto - (selectedPayment.monto_pagado || 0);

    const breakdowns: PropietarioBreakdown[] = [];
    let usedAplicado = 0;

    propietarios.forEach((prop, index) => {
      const pct = (prop.porcentaje_propiedad ?? 100) / 100;
      const isLast = index === propietarios.length - 1;

      // Base rent proportional to ownership
      const valorAlquiler = Math.round(propMontoBase * pct * 100) / 100;

      // Commission: 0 if no_comision, else proportional
      const tenantIrpf = tenantRetention(valorAlquiler, systemIrpfPorcentaje, tenantAgent);
      const comision = prop.no_comision ? 0 : Math.round(valorAlquiler * comisionRate * 100) / 100;
      const ivaComision = Math.round(comision * ivaRate * 100) / 100;

      // Eligibility uses the monthly share, not the partial receipt amount.
      const monthlyRent = Number(selectedPayment.base_alquiler_recibo ?? selectedContract?.monto_alquiler_inquilino ?? 0) * pct;
      const shouldApplyIrpf = !tenantAgent && ownerIrpfStatus(prop, monthlyRent, selectedContract?.moneda_inquilino ?? 'UYU', irpfConfig) === true;
      const retencionIrpf = shouldApplyIrpf ? Math.round(valorAlquiler * irpfRate * 100) / 100 : 0;

      // Shared deductions split proportionally
      const interesMoraShare = Math.round(interesMora * pct * 100) / 100;
      const ivaInteresShare = Math.round(ivaInteres * pct * 100) / 100;
      const otrosShare = Math.round(otrosTotal * pct * 100) / 100;

      // Net = base - all deductions
      const neto = Math.round((valorAlquiler - tenantIrpf - comision - ivaComision - retencionIrpf - interesMoraShare - ivaInteresShare - otrosShare) * 100) / 100;

      // monto_aplicado_pago: proportional share of the pago monto
      // Last propietario absorbs rounding difference
      let montoAplicado: number;
      if (isLast) {
        montoAplicado = Math.round((pagoMonto - usedAplicado) * 100) / 100;
      } else {
        montoAplicado = Math.round(pagoMonto * pct * 100) / 100;
        usedAplicado += montoAplicado;
      }

      breakdowns.push({
        propietario: prop,
        valor_alquiler: valorAlquiler,
        comision,
        iva_comision: ivaComision,
        retencion_irpf: retencionIrpf,
        retencion_irpf_inquilino: tenantIrpf,
        interes_mora_share: interesMoraShare,
        iva_interes_share: ivaInteresShare,
        otros_importes_share: otrosShare,
        neto,
        monto_aplicado_pago: montoAplicado,
      });
    });

    return breakdowns;
  }, [propMontoBase, selectedContract, selectedPayment, systemIrpfPorcentaje, propSharedForm, tenantAgent, irpfConfig]);

  // Total across all propietarios (net amount)
  const totalPropietarioNeto = useMemo(() => {
    return propietarioBreakdowns.reduce((sum, b) => sum + b.neto, 0);
  }, [propietarioBreakdowns]);

  // Load active contracts and system IRPF config
  useEffect(() => {
    loadContracts(1);
    loadSystemIrpfConfig();
  }, []);

  const loadSystemIrpfConfig = async () => {
    try {
      const res = await configService.getAll();
      const values = Object.fromEntries((res.data?.items ?? []).map((item: { clave: string; valor: string }) => [item.clave, item.valor]));
      const rate = Number(values.irpf_porcentaje ?? 10.5);
      if (!Number.isFinite(rate) || rate < 0 || rate >= 100) throw new Error('Porcentaje de IRPF inválido');
      setSystemIrpfPorcentaje(rate);
      setIrpfConfig(values);
      setIrpfConfigLoaded(true);
    } catch (error) {
      console.error('Error loading IRPF config:', error);
      setSystemIrpfPorcentaje(0);
    }
  };

  // Debounce contract search against the full server-side result set.
  useEffect(() => {
    if (skipContractSearchEffect.current) {
      skipContractSearchEffect.current = false;
      return;
    }

    const timer = window.setTimeout(() => {
      loadContracts(1, searchTerm);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  // Auto-compute iva_interes when interes_mora changes (INQUILINO form)
  useEffect(() => {
    const ivaRate = Number(selectedContract?.iva ?? selectedContract?.default_iva ?? 22) / 100;
    const ivaInteres = Math.round(inquilinoForm.interes_mora * ivaRate * 100) / 100;
    setInquilinoForm(prev => ({ ...prev, iva_interes: ivaInteres }));
  }, [inquilinoForm.interes_mora, selectedContract]);

  // Auto-compute iva_interes when interes_mora changes (PROPIETARIO shared form)
  useEffect(() => {
    const ivaRate = Number(selectedContract?.iva ?? selectedContract?.default_iva ?? 22) / 100;
    const ivaInteres = Math.round(propSharedForm.interes_mora * ivaRate * 100) / 100;
    setPropSharedForm(prev => ({ ...prev, iva_interes: ivaInteres }));
  }, [propSharedForm.interes_mora, selectedContract]);

  const loadContracts = async (page = contractPage, search = searchTerm) => {
    setLoadingContracts(true);
    try {
      const res = await contractService.getContracts({
        estado: 'ACTIVO',
        page,
        limit: CONTRACT_PAGE_SIZE,
        search: search.trim() || undefined,
      });
      const data = (res as any).data || res;
      const items = data.items || data;
      setContracts(Array.isArray(items) ? items : []);
      setContractPage(data.page || page);
      setContractTotal(Number(data.total) || (Array.isArray(items) ? items.length : 0));
      setContractTotalPages(Math.max(1, Number(data.totalPages) || 1));
    } catch (error) {
      console.error('Error loading contracts:', error);
      setContracts([]);
      setContractPage(page);
      setContractTotal(0);
      setContractTotalPages(1);
    } finally {
      setLoadingContracts(false);
    }
  };

  const loadPendingPayments = async (contratoId: number, tipo: TipoRecibo) => {
    try {
      const res = await paymentService.getPaymentsByContract(contratoId);
      const allPayments = (res as any).data || res;

      const pending = allPayments
        .filter((p: Pago) =>
          p.tipo === tipo &&
          (p.estado === 'PENDIENTE' || p.estado === 'ATRASADO')
        )
        .map((p: Pago) => ({ ...p, selected: false }));

      setPendingPayments(pending);
    } catch (error) {
      console.error('Error loading pending payments:', error);
      setPendingPayments([]);
    }
  };

  const handleSelectContract = (contract: Contrato) => {
    setTenantResguardo({ confirmado: false, numero: '' });
    setSelectedContract(contract);
    loadPendingPayments(contract.id, tipoRecibo);
    setStep('fill-data');

    // Reset forms
    setSelectedPayment(null);
    setInquilinoForm({
      valor_alquiler: 0,
      interes_mora: 0,
      iva_interes: 0,
      otros_importes: [],
      metodo_pago: ''
    });
    setPropSharedForm({
      metodo_pago: '',
      otros_importes: [],
      interes_mora: 0,
      iva_interes: 0,
      resguardo_confirmado: false,
      resguardo_numero: '',
    });
    setPropMontoBase(0);
  };

  const handleSelectPayment = (payment: PendingPayment) => {
    setSelectedPayment(payment);
    setTenantResguardo({ confirmado: false, numero: '' });

    if (tipoRecibo === 'INQUILINO') {
      setInquilinoForm(prev => ({
        ...prev,
        valor_alquiler: Math.max(0, payment.monto - (payment.monto_pagado || 0))
      }));
    } else {
      // The API resolves the month's gross base, including manual amount edits.
      // Scheduled owner payments still use their gross rent before deductions.
      const montoBase = Number(payment.base_alquiler_recibo ?? selectedContract?.monto_alquiler_inquilino) || payment.monto;
      setPropMontoBase(montoBase * (payment.monto - (payment.monto_pagado || 0)) / payment.monto);
    }
  };

  const handleAddOtroImporteInquilino = () => {
    setInquilinoForm({
      ...inquilinoForm,
      otros_importes: [...inquilinoForm.otros_importes, { descripcion: '', valor: 0 }]
    });
  };

  const handleRemoveOtroImporteInquilino = (index: number) => {
    setInquilinoForm({
      ...inquilinoForm,
      otros_importes: inquilinoForm.otros_importes.filter((_, i) => i !== index)
    });
  };

  const handleAddOtroImportePropietario = () => {
    setPropSharedForm({
      ...propSharedForm,
      otros_importes: [...propSharedForm.otros_importes, { descripcion: '', valor: 0 }]
    });
  };

  const handleRemoveOtroImportePropietario = (index: number) => {
    setPropSharedForm({
      ...propSharedForm,
      otros_importes: propSharedForm.otros_importes.filter((_, i) => i !== index)
    });
  };

  const handlePrintCreated = async (ids = createdReceiptIds, reservedWindow?: Window | null) => {
    const printWindow = reservedWindow ?? reciboService.reservePrintWindow();
    if (!printWindow) {
      setPrintError('Los recibos ya fueron creados. Permita las ventanas emergentes y pulse Reintentar impresión.');
      return;
    }
    try {
      await reciboService.printRecibos(ids, printWindow);
      navigate('/receipts');
    } catch (error) {
      printWindow.close();
      console.error('Error printing created receipts:', error);
      setPrintError('Los recibos ya fueron creados, pero no se pudo abrir la impresión. Puede reintentar sin volver a generarlos.');
    }
  };

  const handleRetryPrint = async () => {
    if (submitLock.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      await handlePrintCreated();
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const handleSubmit = async (printAfterCreate = false) => {
    if (submitLock.current || createdReceiptIds.length > 0) return;
    if (!selectedContract || !selectedPayment) {
      showAlert({
        type: 'warning',
        title: 'Campos requeridos',
        message: 'Debe seleccionar un contrato y un pago pendiente',
      });
      return;
    }

    if (tipoRecibo === 'PROPIETARIO' && propietarioBreakdowns.length === 0) {
      showAlert({ type: 'warning', title: 'Sin propietarios', message: 'No hay propietarios para generar recibos' });
      return;
    }
    if (irpfPending) {
      showAlert({ type: 'warning', title: 'Falta configurar el mínimo de IRPF', message: 'Configure el mínimo imponible de IRPF para la moneda del alquiler en Configuración.' });
      return;
    }
    if (!irpfConfigLoaded) {
      showAlert({ type: 'warning', title: 'Configuración pendiente', message: 'No se pudo cargar el porcentaje de IRPF. Recargue antes de emitir.' });
      return;
    }
    if (tipoRecibo === 'INQUILINO' && tenantAgent && tenantResguardo.confirmado && !tenantResguardo.numero.trim()) {
      showAlert({ type: 'warning', title: 'Falta número de resguardo', message: 'Ingrese el número del resguardo presentado por el inquilino.' });
      return;
    }
    printAfterIssue.current = printAfterCreate;
    setPrintError('');
    setIssueTasks([]);
    setIssueCompleted(false);
    setCanReturnToForm(false);
    issueController.current?.abort();
    issueController.current = new AbortController();
    issuance.current = new ReceiptIssuance({
      load: async id => (await dgiService.getReceiptWorkflow(id)).data,
      generate: id => dgiService.generar(id),
      document: async id => (await dgiService.getDocument(id)).data,
      confirm: id => dgiService.enviar(id),
    }, setIssueTasks, error => getCfeRequestError(error, 'No se pudo completar el paso'), issueController.current.signal);
    createForIssue.current = async () => {
      let ids: number[];
      if (tipoRecibo === 'INQUILINO') {
        const reciboData: any = {
          pago_id: selectedPayment.id,
          metodo_pago: inquilinoForm.metodo_pago,
          monto_recibo: totalInquilino,
          resguardo_confirmado: tenantAgent && tenantResguardo.confirmado,
          resguardo_numero: tenantAgent ? tenantResguardo.numero.trim() || null : null,
          monto_aplicado_pago: inquilinoForm.valor_alquiler,
          desglose: {
            valor_alquiler: inquilinoForm.valor_alquiler,
            interes_mora: inquilinoForm.interes_mora,
            iva_interes: inquilinoForm.iva_interes,
            retencion_irpf: retencionIrpfInquilino,
            agente_retencion_inquilino: tenantAgent,
            otros_importes: inquilinoForm.otros_importes
          }
        };

        const response = await reciboService.createRecibo(reciboData);
        ids = [Number(response.data.id)];
      } else {
        // PROPIETARIO: batch create one receipt per propietario.
        const batchData = {
          pago_id: selectedPayment.id,
          metodo_pago: propSharedForm.metodo_pago,
          recibos: propietarioBreakdowns.map((b) => {
            // Split each otro_importe proportionally
            const pct = (b.propietario.porcentaje_propiedad ?? 100) / 100;
            const otrosProporcionados = propSharedForm.otros_importes.map(item => ({
              descripcion: item.descripcion,
              valor: Math.round((Number(item.valor) || 0) * pct * 100) / 100
            }));

            return {
              propietario_id: b.propietario.id,
              monto_recibo: b.neto,
              monto_aplicado_pago: b.monto_aplicado_pago,
              desglose: {
                valor_alquiler: b.valor_alquiler,
                comision_administracion: b.comision,
                iva_comision: b.iva_comision,
                retencion_irpf: b.retencion_irpf,
                retencion_irpf_inquilino: b.retencion_irpf_inquilino,
                interes_mora: b.interes_mora_share,
                iva_interes: b.iva_interes_share,
                otros_importes: otrosProporcionados
              },
              resguardo_confirmado: propSharedForm.resguardo_confirmado,
              resguardo_numero: propSharedForm.resguardo_numero,
            };
          })
        };

        const response = await reciboService.createReciboBatch(batchData);
        ids = response.data.map((receipt: { id: number }) => Number(receipt.id));
      }

      setCreatedReceiptIds(ids);
      return ids;
    };
    setIssueOpen(true);
    await runIssuance();
  };

  const runIssuance = async () => {
    if (submitLock.current || !issuance.current || !createForIssue.current) return;
    submitLock.current = true;
    setSubmitting(true);
    try {
      await issuance.current.run(createForIssue.current);
      setIssueCompleted(true);
    } catch (error) {
      if (issueController.current?.signal.aborted) return;
      // Only a definite validation rejection makes it safe to submit the creation again.
      const status = (error as { response?: { status?: number } })?.response?.status;
      setCanReturnToForm(status === 400 || status === 422);
    } finally {
      if (!issueController.current?.signal.aborted) {
        setCreatedReceiptIds(issuance.current.ids);
        setSubmitting(false);
      }
      submitLock.current = false;
    }
  };

  const handleIssueContinue = async () => {
    if (!issueCompleted || submitting) return;
    setIssueOpen(false);
    if (printAfterIssue.current) await handleRetryPrint();
    else navigate('/receipts');
  };

  const formatCurrency = (amount: number, moneda?: string) => {
    return `${moneda || 'UYU'} ${amount.toLocaleString('es-UY', { minimumFractionDigits: 2 })}`;
  };

  return (
    <MainLayout>
      {issueOpen && <ReceiptIssueDialog tasks={issueTasks} running={submitting} completed={issueCompleted}
        saved={createdReceiptIds.length > 0} canReturnToForm={canReturnToForm}
        onRetry={() => { void runIssuance(); }} onContinue={() => { void handleIssueContinue(); }}
        onReturn={() => {
          if (canReturnToForm) setIssueOpen(false);
          else navigate('/receipts');
        }} />}
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Crear Nuevo Recibo</h1>
            <p className="text-gray-600 mt-1">
              {step === 'select-contract' ? 'Seleccione un contrato' : 'Complete los datos del recibo'}
            </p>
          </div>
          <Button variant="secondary" onClick={() => navigate('/receipts')}>
            ← Volver
          </Button>
        </div>

        {/* Step 1: Select Contract and Type */}
        {step === 'select-contract' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Type selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Recibo
              </label>
              <div className="flex gap-4">
                <button
                  onClick={() => setTipoRecibo('INQUILINO')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    tipoRecibo === 'INQUILINO'
                      ? 'bg-purple-100 text-purple-800 border-2 border-purple-300'
                      : 'bg-gray-100 text-gray-700 border-2 border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  Recibo para Inquilino
                </button>
                <button
                  onClick={() => setTipoRecibo('PROPIETARIO')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    tipoRecibo === 'PROPIETARIO'
                      ? 'bg-orange-100 text-orange-800 border-2 border-orange-300'
                      : 'bg-gray-100 text-gray-700 border-2 border-gray-200 hover:bg-gray-200'
                  }`}
                >
                  Recibo para Propietario
                </button>
              </div>
            </div>

            {/* Contract search */}
            <div className="mb-4">
              <Input
                label="Buscar Contrato"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por número, inquilino o dirección..."
              />
              {loadingContracts && searchTerm.trim() && (
                <p className="text-sm text-gray-500 mt-1">Buscando...</p>
              )}
              {searchTerm.trim() && !loadingContracts && (
                <p className="text-sm text-gray-600 mt-1">
                  {contractTotal} {contractTotal === 1 ? 'contrato encontrado' : 'contratos encontrados'}
                </p>
              )}
            </div>

            {/* Contracts list */}
            {loadingContracts ? (
              <p className="text-gray-500 text-center py-8">Cargando contratos...</p>
            ) : contracts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No se encontraron contratos activos</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {contracts.map((contract) => (
                  <div
                    key={contract.id}
                    onClick={() => handleSelectContract(contract)}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-400 cursor-pointer transition-all"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-900">{contract.numero_contrato}</p>
                        <p className="text-sm text-gray-600">Inquilino: {contract.inquilino_nombre}</p>
                        <p className="text-sm text-gray-500">{contract.inmueble_direccion}</p>
                      </div>
                      <span className="text-sm font-medium text-blue-600">Seleccionar →</span>
                    </div>
                  </div>
                ))}
                <div className="sticky bottom-0 mt-4 flex flex-col gap-3 border-t border-gray-100 bg-white pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-600">
                    Mostrando {((contractPage - 1) * CONTRACT_PAGE_SIZE) + 1} a {Math.min(contractPage * CONTRACT_PAGE_SIZE, contractTotal)} de {contractTotal} contratos
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={contractPage <= 1 || loadingContracts}
                      onClick={() => loadContracts(contractPage - 1, searchTerm)}
                    >
                      Anterior
                    </Button>
                    <span className="px-2 text-sm text-gray-700">
                      Página {contractPage} de {contractTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={contractPage >= contractTotalPages || loadingContracts}
                      onClick={() => loadContracts(contractPage + 1, searchTerm)}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Fill Data */}
        {step === 'fill-data' && selectedContract && (
          <div className="space-y-6">
            {/* Contract Info */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Información del Contrato</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Código Inmueble</p>
                  <p className="font-medium">{selectedContract.inmueble_padron || selectedContract.inmueble_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Contrato</p>
                  <p className="font-medium">{selectedContract.numero_contrato}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Inquilino</p>
                  <p className="font-medium">{selectedContract.inquilino_nombre}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Correlativo</p>
                  <p className="font-medium">{selectedContract.correlativo || '-'}</p>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setStep('select-contract')}
                className="mt-4"
              >
                ← Cambiar Contrato
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pending Payments */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Pendientes de Pago</h2>
                {pendingPayments.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No hay pagos pendientes</p>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-4 gap-2 text-sm font-medium text-gray-700 pb-2 border-b">
                      <span>No.</span>
                      <span>Fecha</span>
                      <span>{tipoRecibo === 'INQUILINO' ? 'Inquilino' : 'Propietario'}</span>
                      <span className="text-right">Valor</span>
                    </div>
                    {pendingPayments.map((payment) => {
                      const personName = tipoRecibo === 'INQUILINO'
                        ? (payment.inquilino_nombre || selectedContract?.inquilino_nombre || '-')
                        : (payment.propietario_nombre || '-');

                      return (
                        <div
                          key={payment.id}
                          onClick={() => handleSelectPayment(payment)}
                          className={`grid grid-cols-4 gap-2 text-sm p-2 rounded cursor-pointer transition-colors border ${
                            selectedPayment?.id === payment.id
                              ? 'bg-blue-100 border-blue-300'
                              : 'border-transparent hover:bg-gray-50'
                          }`}
                        >
                          <span className="font-medium">#{payment.id}</span>
                          <span className="text-gray-700">{formatLocalDate(payment.fecha_limite)}</span>
                          <span className="text-gray-900 text-xs truncate" title={personName}>
                            {personName}
                            {Boolean(payment.es_pago_parcial) && (
                              <span className="ml-1 inline-flex items-center px-1 py-0.5 rounded text-xs bg-amber-100 text-amber-800" title={`${payment.dias_ocupados || 0} días`}>
                                Parcial
                              </span>
                            )}
                          </span>
                          <span className="text-right font-medium text-gray-900">
                            {payment.moneda} {payment.monto.toLocaleString('es-UY', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Form Fields */}
              {selectedPayment && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Valores del Recibo</h2>

                {tipoRecibo === 'INQUILINO' ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valor del Alquiler ({selectedPayment?.moneda || 'UYU'})
                      </label>
                      <Input
                        type="number"
                        value={inquilinoForm.valor_alquiler}
                        onChange={(e) => setInquilinoForm({ ...inquilinoForm, valor_alquiler: Number(e.target.value) })}
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Interés por Mora ({selectedPayment?.moneda || 'UYU'})
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={inquilinoForm.interes_mora}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          setInquilinoForm(prev => ({ ...prev, interes_mora: Math.abs(val) }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        IVA Interés ({selectedPayment?.moneda || 'UYU'})
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={inquilinoForm.iva_interes}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          setInquilinoForm(prev => ({ ...prev, iva_interes: Math.abs(val) }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        IRPF retenido por el inquilino ({tenantAgent ? systemIrpfPorcentaje : 0}%) — {selectedPayment?.moneda || 'UYU'}
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={retencionIrpfInquilino}
                        disabled
                        placeholder="0.00"
                      />
                    </div>

                    {tenantAgent && (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">La retención se descuenta del dinero recibido. El alquiler se cancela por el importe bruto; el resguardo queda pendiente hasta confirmar su entrega.</p>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" checked={tenantResguardo.confirmado} onChange={e => setTenantResguardo(prev => ({ ...prev, confirmado: e.target.checked }))} />
                          El inquilino presentó el resguardo
                        </label>
                        <label className="block text-sm">Número de resguardo
                          <Input value={tenantResguardo.numero} onChange={e => setTenantResguardo(prev => ({ ...prev, numero: e.target.value }))} maxLength={50} />
                        </label>
                      </div>
                    )}

                    {/* Otros Importes */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Otros Importes ({selectedPayment?.moneda || 'UYU'})
                        </label>
                        <Button size="sm" variant="secondary" onClick={handleAddOtroImporteInquilino}>
                          + Agregar
                        </Button>
                      </div>
                      {inquilinoForm.otros_importes.map((item, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={item.descripcion}
                            onChange={(e) => {
                              const newItems = [...inquilinoForm.otros_importes];
                              newItems[index].descripcion = e.target.value;
                              setInquilinoForm(prev => ({ ...prev, otros_importes: newItems }));
                            }}
                            placeholder="Descripción"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={item.valor}
                            onChange={(e) => {
                              const newItems = [...inquilinoForm.otros_importes];
                              const val = parseFloat(e.target.value) || 0;
                              newItems[index].valor = Math.abs(val);
                              setInquilinoForm(prev => ({ ...prev, otros_importes: newItems }));
                            }}
                            placeholder="Valor"
                            className="w-32 px-3 py-2 border border-gray-300 rounded-md"
                          />
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleRemoveOtroImporteInquilino(index)}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Método de Pago
                      </label>
                      <select
                        value={inquilinoForm.metodo_pago}
                        onChange={(e) => setInquilinoForm({ ...inquilinoForm, metodo_pago: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccione un método de pago</option>
                        {METODOS_PAGO.map((metodo) => (
                          <option key={metodo} value={metodo}>
                            {metodo}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="pt-4 border-t">
                      <p className="text-sm text-gray-600">Total a Pagar</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(totalInquilino, selectedPayment?.moneda)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Propietarios breakdown (read-only) */}
                    {selectedContract.propietarios && selectedContract.propietarios.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Propietarios del Inmueble
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Se generará un recibo por cada propietario en base a su porcentaje de propiedad
                        </p>
                        <div className="space-y-3">
                          {propietarioBreakdowns.map((breakdown) => {
                            const prop = breakdown.propietario;
                            const propName = prop.nombre_completo || `${prop.primer_nombre || ''} ${prop.primer_apellido || ''}`.trim();
                            return (
                              <div
                                key={prop.id}
                                className="p-3 border border-gray-200 rounded-lg bg-gray-50"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm text-gray-900">{propName}</span>
                                    <span className="text-xs text-gray-500">{prop.documento}</span>
                                  </div>
                                  <span className="text-sm font-semibold text-blue-700">
                                    {prop.porcentaje_propiedad ?? 100}%
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {Boolean(prop.no_comision) && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
                                      Sin comisión
                                    </span>
                                  )}
                                  {tenantAgent ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">Retiene el inquilino</span>
                                  ) : breakdown.retencion_irpf > 0 ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
                                      Con IRPF ({systemIrpfPorcentaje}%)
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                                      {irpfPending ? 'Falta configurar mínimo IRPF' : 'Sin retención de IRPF'}
                                    </span>
                                  )}
                                </div>
                                {selectedPayment && propMontoBase > 0 && (
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                                    <span>Base alquiler:</span>
                                    <span className="text-right">{formatCurrency(breakdown.valor_alquiler, selectedPayment.moneda)}</span>
                                    {breakdown.comision > 0 && (
                                      <>
                                        <span>Comisión ({selectedContract.comision}%):</span>
                                        <span className="text-right text-red-600">-{formatCurrency(breakdown.comision, selectedPayment.moneda)}</span>
                                      </>
                                    )}
                                    {breakdown.iva_comision > 0 && (
                                      <>
                                        <span>IVA comisión:</span>
                                        <span className="text-right text-red-600">-{formatCurrency(breakdown.iva_comision, selectedPayment.moneda)}</span>
                                      </>
                                    )}
                                    {breakdown.retencion_irpf_inquilino > 0 && (
                                      <><span>IRPF retenido por inquilino:</span><span className="text-right text-red-600">-{formatCurrency(breakdown.retencion_irpf_inquilino, selectedPayment.moneda)}</span></>
                                    )}
                                    {breakdown.retencion_irpf > 0 && (
                                      <>
                                        <span>IRPF:</span>
                                        <span className="text-right text-red-600">-{formatCurrency(breakdown.retencion_irpf, selectedPayment.moneda)}</span>
                                      </>
                                    )}
                                    {breakdown.interes_mora_share > 0 && (
                                      <>
                                        <span>Interés mora:</span>
                                        <span className="text-right text-red-600">-{formatCurrency(breakdown.interes_mora_share, selectedPayment.moneda)}</span>
                                      </>
                                    )}
                                    {breakdown.iva_interes_share > 0 && (
                                      <>
                                        <span>IVA interés:</span>
                                        <span className="text-right text-red-600">-{formatCurrency(breakdown.iva_interes_share, selectedPayment.moneda)}</span>
                                      </>
                                    )}
                                    {breakdown.otros_importes_share > 0 && (
                                      <>
                                        <span>Otros importes:</span>
                                        <span className="text-right text-red-600">-{formatCurrency(breakdown.otros_importes_share, selectedPayment.moneda)}</span>
                                      </>
                                    )}
                                    <span className="font-semibold text-gray-900 pt-1 border-t">Neto a pagar:</span>
                                    <span className="text-right font-semibold text-gray-900 pt-1 border-t">
                                      {formatCurrency(breakdown.neto, selectedPayment.moneda)}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {selectedContract.propietarios.length === 0 && (
                            <p className="text-gray-500 text-sm">No hay propietarios asociados a este contrato</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Shared editable fields */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Valor Base del Alquiler ({selectedPayment?.moneda || 'UYU'})
                      </label>
                      <Input
                        type="number"
                        value={propMontoBase}
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Interés por Mora - compartido ({selectedPayment?.moneda || 'UYU'})
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={propSharedForm.interes_mora}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          setPropSharedForm(prev => ({ ...prev, interes_mora: Math.abs(val) }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        IVA Interés - compartido ({selectedPayment?.moneda || 'UYU'})
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        value={propSharedForm.iva_interes}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          setPropSharedForm(prev => ({ ...prev, iva_interes: Math.abs(val) }));
                        }}
                        placeholder="0.00"
                      />
                    </div>

                    {/* Otros Importes (shared, split proportionally) */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Otros Importes - compartido ({selectedPayment?.moneda || 'UYU'})
                        </label>
                        <Button size="sm" variant="secondary" onClick={handleAddOtroImportePropietario}>
                          + Agregar
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">
                        Se dividen proporcionalmente entre propietarios según su porcentaje de propiedad
                      </p>
                      {propSharedForm.otros_importes.map((item, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={item.descripcion}
                            onChange={(e) => {
                              const newItems = [...propSharedForm.otros_importes];
                              newItems[index].descripcion = e.target.value;
                              setPropSharedForm(prev => ({ ...prev, otros_importes: newItems }));
                            }}
                            placeholder="Descripción"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                          />
                          <input
                            type="number"
                            step="0.01"
                            value={item.valor}
                            onChange={(e) => {
                              const newItems = [...propSharedForm.otros_importes];
                              const val = parseFloat(e.target.value) || 0;
                              newItems[index].valor = Math.abs(val);
                              setPropSharedForm(prev => ({ ...prev, otros_importes: newItems }));
                            }}
                            placeholder="Valor"
                            className="w-32 px-3 py-2 border border-gray-300 rounded-md"
                          />
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleRemoveOtroImportePropietario(index)}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Método de Pago
                      </label>
                      <select
                        value={propSharedForm.metodo_pago}
                        onChange={(e) => setPropSharedForm({ ...propSharedForm, metodo_pago: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccione un método de pago</option>
                        {METODOS_PAGO.map((metodo) => (
                          <option key={metodo} value={metodo}>
                            {metodo}
                          </option>
                        ))}
                      </select>
                    </div>

                    {!tenantAgent && <>
                    <div className="flex items-center gap-2">
                      <input
                        id="resguardo_confirmado"
                        type="checkbox"
                        checked={!!propSharedForm.resguardo_confirmado}
                        onChange={(e) => setPropSharedForm({ ...propSharedForm, resguardo_confirmado: e.target.checked })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="resguardo_confirmado" className="text-sm font-medium text-gray-700">
                        Resguardo presentado por el propietario
                      </label>
                    </div>

                    {propSharedForm.resguardo_confirmado && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Número de resguardo
                        </label>
                        <Input
                          type="text"
                          value={propSharedForm.resguardo_numero}
                          onChange={(e) => setPropSharedForm({ ...propSharedForm, resguardo_numero: e.target.value })}
                          placeholder="Ej: 123456"
                        />
                      </div>
                    )}

                    </>}

                    <div className="pt-4 border-t">
                      <p className="text-sm text-gray-600">Total Neto a Pagar (todos los propietarios)</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatCurrency(totalPropietarioNeto, selectedPayment?.moneda)}
                      </p>
                      {propietarioBreakdowns.length > 1 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Se generarán {propietarioBreakdowns.length} recibos individuales
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>

            {/* Notes and Actions */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Nota:</strong> La serie y el número del recibo se generan automáticamente al crear el recibo.
                {tipoRecibo === 'INQUILINO'
                  ? ' Para recibos de inquilino, los intereses, IVA e importes adicionales se suman al alquiler. El IRPF retenido por el inquilino se descuenta.'
                  : ' Para recibos de propietario, se genera un recibo por cada propietario en base a su porcentaje de propiedad. Las comisiones, impuestos, IRPF e importes adicionales se RESTAN del monto base proporcionalmente.'}
              </p>
            </div>

            {irpfPending && (
              <div role="alert" className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
                Configure el mínimo imponible de IRPF para la moneda del alquiler antes de emitir recibos.
              </div>
            )}
            {printError && (
              <div role="alert" className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 flex flex-wrap items-center justify-between gap-3">
                <p>{printError}</p>
                <Button onClick={handleRetryPrint} disabled={submitting}>
                  {submitting ? 'Preparando impresión...' : 'Reintentar impresión'}
                </Button>
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-4">
              <Button
                variant="secondary"
                onClick={() => setStep('select-contract')}
                disabled={submitting || createdReceiptIds.length > 0}
              >
                Cancelar
              </Button>
              <Button
                onClick={() => handleSubmit()}
                disabled={submitting || !selectedPayment || irpfPending || createdReceiptIds.length > 0}
              >
                {submitting ? 'Generando...' : tipoRecibo === 'PROPIETARIO' && propietarioBreakdowns.length > 1
                  ? `Generar ${propietarioBreakdowns.length} Recibos`
                  : 'Generar Recibo'}
              </Button>
              <Button
                variant="success"
                onClick={() => handleSubmit(true)}
                disabled={submitting || !selectedPayment || irpfPending || createdReceiptIds.length > 0}
              >
                {submitting ? 'Procesando...' : 'Generar e imprimir'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
